import type { NetworkRequestEntry } from "@ark-snitch/shared";

const MAX_ENTRIES = 50;
const buffer: NetworkRequestEntry[] = [];
let installed = false;

const push = (entry: NetworkRequestEntry) => {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_ENTRIES);
  }
};

const isWidgetRequest = (url: string): boolean =>
  url.includes("/api/snitch/");

// Try to monkey-patch fetch + XHR for full fidelity (URL, method, status,
// duration). Some hosts freeze the browser runtime (e.g. oidc-spa's
// browserRuntimeFreeze) and reject these assignments — in that case we
// fall back to PerformanceObserver, which gives us URLs + timing without
// HTTP status codes. The widget never crashes either way.
const tryPatchFetch = (): boolean => {
  try {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      const method = (init?.method || (input as Request).method || "GET").toUpperCase();
      const start = performance.now();
      const ts = Date.now();
      try {
        const res = await originalFetch(input, init);
        if (!isWidgetRequest(url)) {
          push({
            url,
            method,
            status: res.status,
            durationMs: Math.round(performance.now() - start),
            ts,
            initiator: "fetch",
          });
        }
        return res;
      } catch (err) {
        if (!isWidgetRequest(url)) {
          push({
            url,
            method,
            status: 0,
            durationMs: Math.round(performance.now() - start),
            ts,
            initiator: "fetch",
          });
        }
        throw err;
      }
    };
    return true;
  } catch {
    return false;
  }
};

const tryPatchXHR = (): boolean => {
  try {
    const OriginalXHR = window.XMLHttpRequest;
    type Tracked = XMLHttpRequest & {
      __snitch?: { url: string; method: string; ts: number; start: number };
    };
    const open = OriginalXHR.prototype.open;
    const send = OriginalXHR.prototype.send;

    OriginalXHR.prototype.open = function (
      this: Tracked,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      this.__snitch = {
        method: method.toUpperCase(),
        url: typeof url === "string" ? url : url.href,
        ts: Date.now(),
        start: performance.now(),
      };
      // @ts-expect-error — pass through original signature
      return open.call(this, method, url, ...rest);
    };

    OriginalXHR.prototype.send = function (this: Tracked, body?: Document | XMLHttpRequestBodyInit | null) {
      const meta = this.__snitch;
      if (meta && !isWidgetRequest(meta.url)) {
        this.addEventListener("loadend", () => {
          push({
            url: meta.url,
            method: meta.method,
            status: this.status,
            durationMs: Math.round(performance.now() - meta.start),
            ts: meta.ts,
            initiator: "xhr",
          });
        });
      }
      return send.call(this, body ?? null);
    };
    return true;
  } catch {
    return false;
  }
};

const installPerfObserverFallback = (): void => {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const navStart = Date.now() - Math.round(performance.now());
    const observer = new PerformanceObserver((list) => {
      for (const e of list.getEntries() as PerformanceResourceTiming[]) {
        const it = e.initiatorType;
        if (it !== "fetch" && it !== "xmlhttprequest") continue;
        if (isWidgetRequest(e.name)) continue;
        push({
          url: e.name,
          method: "?", // not exposed by Resource Timing
          // status not exposed by Resource Timing — leave undefined
          durationMs: Math.round(e.duration),
          ts: Math.round(navStart + e.startTime),
          initiator: it === "fetch" ? "fetch" : "xhr",
        });
      }
    });
    observer.observe({ entryTypes: ["resource"], buffered: true });
  } catch {
    /* observer not supported — give up silently */
  }
};

export const installNetworkBuffer = (): void => {
  if (installed) return;
  installed = true;

  const fetchPatched = tryPatchFetch();
  const xhrPatched = tryPatchXHR();

  if (!fetchPatched || !xhrPatched) {
    installPerfObserverFallback();
  }
};

export const snapshotNetworkBuffer = (): NetworkRequestEntry[] => buffer.slice();
